// Handle rules — plan §6 (Handle / slug), §17 edge cases and §19 (impersonation).

export const HANDLE_MIN_LENGTH = 3;
export const HANDLE_MAX_LENGTH = 20;
export const HANDLE_PATTERN = /^[a-z0-9_]{3,20}$/;

/**
 * Handles that would collide with routes, staff or the brand. Checked for every Actor kind.
 * One-character entries (`b`, `c`, `p`) can never match the length rule but document the
 * URL prefixes they protect.
 */
export const RESERVED_HANDLES: ReadonlySet<string> = new Set([
  "about",
  "account",
  "admin",
  "api",
  "app",
  "auth",
  "b",
  "billing",
  "blog",
  "business",
  "businesses",
  "c",
  "careers",
  "communities",
  "community",
  "dashboard",
  "dev",
  "docs",
  "email",
  "explore",
  "feed",
  "help",
  "invite",
  "jobs",
  "join",
  "legal",
  "login",
  "logout",
  "mail",
  "marketplace",
  "me",
  "messages",
  "mod",
  "moderator",
  "new",
  "notifications",
  "null",
  "official",
  "onboarding",
  "p",
  "press",
  "privacy",
  "resume",
  "roblox",
  "root",
  "security",
  "sell",
  "settings",
  "sign_in",
  "sign_up",
  "signin",
  "signup",
  "spaces",
  "staff",
  "status",
  "store",
  "studio",
  "studios",
  "studside",
  "support",
  "system",
  "terms",
  "undefined",
  "welcome",
  "www",
]);

export type HandleStatus = "invalid" | "reserved" | "taken" | "available";

/** Handles are case-insensitive; the canonical form is lower case with no surrounding space. */
export function normalizeHandle(value: string): string {
  return value.trim().toLowerCase();
}

export function isValidHandle(value: string): boolean {
  return HANDLE_PATTERN.test(value) && !RESERVED_HANDLES.has(value);
}

export function handleStatus(value: string, taken: boolean): HandleStatus {
  if (!HANDLE_PATTERN.test(value)) return "invalid";
  if (RESERVED_HANDLES.has(value)) return "reserved";
  return taken ? "taken" : "available";
}

/**
 * A Roblox username is only a suggestion for the claim step. Returns `null` when it cannot be
 * used as a handle, so the field starts empty instead of pre-filled with something invalid.
 */
export function normalizeRobloxUsername(username: string): string | null {
  const candidate = username.toLowerCase();
  return isValidHandle(candidate) ? candidate : null;
}
